import { WEB_ASSETS } from '@tenfour/contracts'
import s from './ComposerSendButton.module.css'

interface Props {
  disabled?: boolean
  /**
   * Names the control in place: the word unfurls leftward out from behind the
   * medallion on hover or keyboard focus, pushing whatever sits to its left
   * further left. Replaces the native `title` tooltip when supplied.
   */
  hoverLabel?: string
  /** Home Scribe uses a plate-free idle mark, then the filled mark while held. */
  idleUntilPress?: boolean
  label?: string
  mode?: 'send' | 'stop'
  onClick?: () => void
  type?: 'button' | 'submit'
  /**
   * Medallion diameter in px. The composer dock uses the 40px default (44px on
   * phones); the queue decision panes' exit tools use the design system's 36px.
   */
  size?: number
  /**
   * How a disabled send reads: `gray` swaps in the authored neutral artwork;
   * `dim` keeps the filled navy medallion and fades it to .38 — the design
   * system's Aria composer and decision-pane rest state.
   */
  disabledArtwork?: 'gray' | 'dim'
}

interface ResponsiveAsset {
  oneX: string
  twoX: string
}

function ComposerArtwork({
  asset,
  className,
}: {
  asset: ResponsiveAsset
  className: string
}) {
  return (
    <img
      src={asset.oneX}
      srcSet={`${asset.oneX} 1x, ${asset.twoX} 2x`}
      width={64}
      height={64}
      alt=""
      className={`${s.asset} ${className}`}
      decoding="async"
      aria-hidden="true"
      draggable={false}
    />
  )
}

/**
 * Theme-aware clay composer control shared by customer replies and Aria.
 * Disabled send uses authored neutral artwork; a live inference stays enabled
 * and swaps to the stop-square state so the current run remains cancelable.
 */
export function ComposerSendButton({
  disabled = false,
  hoverLabel,
  idleUntilPress = false,
  label,
  mode = 'send',
  onClick,
  type = 'button',
  size,
  disabledArtwork = 'gray',
}: Props) {
  const composer = WEB_ASSETS.icons.composer
  const showIdleUntilPress = idleUntilPress && mode === 'send'
  const assets =
    mode === 'stop'
      ? composer.stop
      : disabled && disabledArtwork === 'gray'
        ? {
            light: composer.send.disabledLight,
            dark: composer.send.disabledDark,
          }
        : composer.send
  const accessibleLabel = label ?? (mode === 'stop' ? 'Stop response' : 'Send')

  return (
    <button
      type={type}
      className={s.button}
      disabled={disabled}
      data-mode={mode}
      data-idle-until-press={showIdleUntilPress || undefined}
      data-labeled={hoverLabel ? true : undefined}
      data-dim={disabledArtwork === 'dim' || undefined}
      aria-label={accessibleLabel}
      title={hoverLabel ? undefined : accessibleLabel}
      onClick={onClick}
    >
      {hoverLabel && (
        <span className={s.label} aria-hidden="true">
          <span className={s.labelText}>{hoverLabel}</span>
        </span>
      )}
      <span className={s.iconSlot} style={size ? { width: size, height: size } : undefined}>
        {showIdleUntilPress ? (
          <>
            <ComposerArtwork
              asset={composer.send.idle.light}
              className={`${s.light} ${s.idleAsset}`}
            />
            <ComposerArtwork
              asset={composer.send.idle.dark}
              className={`${s.dark} ${s.idleAsset}`}
            />
            <ComposerArtwork
              asset={composer.send.light}
              className={`${s.light} ${s.activeAsset}`}
            />
            <ComposerArtwork
              asset={composer.send.dark}
              className={`${s.dark} ${s.activeAsset}`}
            />
          </>
        ) : (
          <>
            <ComposerArtwork asset={assets.light} className={s.light} />
            <ComposerArtwork asset={assets.dark} className={s.dark} />
          </>
        )}
      </span>
    </button>
  )
}
